import { apiFetch } from '@/lib/api';
import type { RemoveBgJob, RemoveBgHistoryItem } from '@/types/removebg';
import { saveJobToCookies, getHistoryFromCookies, deleteJobFromCookies, cookieItemToJob } from '@/utils/removebg/cookieStorage';

/**
 * Save background removal job
 * - If authenticated: saves to database via API
 * - If not authenticated: saves to cookies
 */
export async function saveBackgroundRemovalJob(
    job: RemoveBgJob,
    isAuthenticated: boolean
): Promise<void> {
    if (isAuthenticated) {
        // Save to database via API
        try {
            await apiFetch('/background/history', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    job_id: job.job_id,
                    url_original: job.url_original || '',
                    url_preview: job.url_preview,
                    url_hd: job.url_hd,
                    processing_time_ms: Math.round(job.processing_time_ms || 0), // Ensure integer for database
                    image_width: 0, // Will be extracted from actual image processing
                    image_height: 0,
                    image_format: job.original?.mimetype?.split('/')[1] || 'png',
                    original_size_bytes: job.original?.size_bytes || 0,
                    result_size_bytes: 0
                })
            });
        } catch (error) {
            console.error('Error saving to database:', error);
            // Fallback to cookies
            saveJobToCookies(job);
        }
    } else {
        // Save to cookies
        saveJobToCookies(job);
    }
}

/**
 * Get background removal history
 * - If authenticated: fetches from database via API
 * - If not authenticated: reads from cookies
 */
export async function getBackgroundRemovalHistory(
    limit: number,
    isAuthenticated: boolean
): Promise<RemoveBgHistoryItem[]> {
    if (isAuthenticated) {
        try {
            const response = await apiFetch<{ history: any[] }>(`/background/history?limit=${limit}`);
            return response.history.map((item: any) => ({
                job_id: item.job_id,
                url_original: item.url_original,
                url_preview: item.url_preview || item.url_hd,
                url_hd: item.url_hd || item.url_preview,
                processing_time_ms: item.processing_time_ms,
                created_at: item.created_at,
                status: 'success' as const,
                original: {
                    filename: `image_${item.job_id.slice(-6)}.${item.image_format || 'png'}`,
                    size_bytes: item.original_size_bytes || 0,
                    mimetype: `image/${item.image_format || 'png'}`
                }
            }));
        } catch (error) {
            console.error('Error fetching from database:', error);
            // Fallback to cookies
            return getHistoryFromCookies().map(cookieItemToJob);
        }
    } else {
        return getHistoryFromCookies().slice(0, limit).map(cookieItemToJob);
    }
}

/**
 * Delete background removal job from history
 * - If authenticated: deletes from database via API
 * - If not authenticated: removes from cookies
 */
export async function deleteBackgroundRemovalJob(
    jobId: string,
    isAuthenticated: boolean
): Promise<void> {
    if (isAuthenticated) {
        try {
            await apiFetch(`/background/history/${jobId}`, {
                method: 'DELETE'
            });
        } catch (error) {
            console.error('Error deleting from database:', error);
            // Fallback to cookies
            deleteJobFromCookies(jobId);
        }
    } else {
        deleteJobFromCookies(jobId);
    }
}

/**
 * Process image and remove background
 * This function should be called by the actual background removal component
 * when it processes the image. This is just a helper for future integration.
 */
export async function removeBackgroundImage(
    imageUrl: string,
    filename: string
): Promise<RemoveBgJob> {
    try {
        const response = await apiFetch<RemoveBgJob>('/background/remove', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                imageUrl,
                filename
            })
        });
        return response;
    } catch (error) {
        console.error('Error processing image:', error);
        throw error;
    }
}
